import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Loader2, Pause, Play, Target, Wallet, X, ShieldAlert } from 'lucide-react';
import toast from 'react-hot-toast';
import { steuereAds, type AdsAktion, type CampaignStat } from '@/lib/analytics-api';

/**
 * Kampagnen und Anzeigengruppen direkt aus dem Dashboard steuern.
 *
 * Nicos Anlass: Pausieren, wieder anschalten oder die Gebotsstrategie
 * wechseln ging nur im Ads-Konto — und fehlte danach im Monatsbericht, weil
 * das LMS nur mitbekommt, was über seine Empfehlungen läuft.
 *
 * Der Begründungs-Dialog ist deshalb kein Beiwerk, sondern der eigentliche
 * Zweck: Aus dem Satz baut der Bericht die Erklärung für den Kunden. Ohne
 * ihn geht die Änderung nicht durch.
 */

type Vorhaben = { beschriftung: string; vorschlag: string } & (
  | { typ: 'kampagne_status'; kampagne: string; status: 'PAUSED' | 'ENABLED' }
  | { typ: 'anzeigengruppe_status'; id: string; name: string; status: 'PAUSED' | 'ENABLED' }
  | { typ: 'gebotsstrategie'; kampagne: string; strategie: 'MAXIMIZE_CLICKS' | 'MAXIMIZE_CONVERSIONS'; gezaehlteConversions?: number | null }
  | { typ: 'budget'; kampagne: string; aktuell: number | null }
  | { typ: 'anzeigen_bereinigen'; kampagne: string; ersatztext: string }
);

/**
 * Begründung aus den Zahlen der Zeile vorschlagen.
 *
 * Bewusst aus echten Werten zusammengesetzt und NICHT von einer KI erfunden:
 * Der Satz landet unverändert im Monatsbericht des Kunden, also darf darin
 * nichts stehen, was die Daten nicht hergeben. Er ist ein Entwurf zum
 * Überschreiben, kein Automatismus — deshalb steht er als echter Text im
 * Feld und nicht als grauer Platzhalter, den man versehentlich für einen
 * Vorschlag hält.
 */
function baueVorschlag(row: CampaignStat, art: string, ziel?: string): string {
  const kosten = row.cost_eur ? `${new Intl.NumberFormat('de-DE', { maximumFractionDigits: 0 }).format(row.cost_eur)} €` : null;
  const klicks = row.ads_clicks || 0;
  const kontakte = row.kontakte ?? row.leads ?? 0;
  const name = row.ad_group_name || row.angle || row.utm_campaign || '';

  const bilanz = () => {
    if (kosten && kontakte === 0) return `${kosten} in 30 Tagen bei ${klicks} Klicks — ohne eine einzige Kontaktanfrage`;
    if (kosten) return `${kosten} in 30 Tagen bei ${klicks} Klicks und ${kontakte} Kontaktanfrage${kontakte === 1 ? '' : 'n'}`;
    return `${klicks} Klicks, ${kontakte} Kontaktanfrage${kontakte === 1 ? '' : 'n'} in 30 Tagen`;
  };

  if (art === 'anzeigengruppe_status' && ziel === 'PAUSED') {
    return `Die Anzeigengruppe „${name}" kostet Geld ohne Ergebnis: ${bilanz()}. Deshalb pausiert, bis die Suchbegriffe passen.`;
  }
  if (art === 'anzeigengruppe_status') {
    return `Die Anzeigengruppe „${name}" wird wieder aktiviert.`;
  }
  if (art === 'kampagne_status' && ziel === 'PAUSED') {
    return `Die Kampagne „${name}" liefert nicht: ${bilanz()}. Deshalb pausiert, statt weiter Budget zu binden.`;
  }
  if (art === 'kampagne_status') {
    return `Die Kampagne „${name}" wird wieder aktiviert.`;
  }
  if (art === 'gebotsstrategie' && ziel === 'MAXIMIZE_CONVERSIONS') {
    // Maßgeblich ist, was GOOGLE zählt — nicht unsere eigenen Kontakte.
    // Lehre vom 26.08.2026: Der alte Text argumentierte mit unseren
    // Kontaktanfragen („genug Signal"), obwohl im Konto 0 Conversions
    // zählten. Die Umstellung nahm der Automatik jedes Ziel, die
    // Fenster-Kampagne lieferte am Folgetag 0 Klicks aus.
    const gezaehlt = row.ads_conversions_30d ?? 0;
    return gezaehlt > 0
      ? `Google zählt für die Kampagne ${gezaehlt} Conversion${gezaehlt === 1 ? '' : 's'} in 30 Tagen — die Automatik hat damit ein echtes Signal und optimiert auf Anfragen statt auf möglichst billige Klicks.`
      : `Umstellung auf Anfragen als Ziel. Hinweis: Google zählt für diese Kampagne aktuell keine Conversions — die Umstellung sollte erst erfolgen, wenn das Zählen nachweislich funktioniert.`;
  }
  if (art === 'gebotsstrategie') {
    return `Zurück auf Klicks als Ziel: ${bilanz()} — für eine Optimierung auf Anfragen fehlt der Automatik noch die Datengrundlage.`;
  }
  if (art === 'budget') {
    return `Budget angepasst: ${bilanz()}.`;
  }
  return '';
}

export function useAdsSteuerung(projectId: string | null | undefined, onFertig?: () => void) {
  const [vorhaben, setVorhaben] = useState<Vorhaben | null>(null);
  const dialog = vorhaben ? (
    <SteuerDialog
      vorhaben={vorhaben}
      projectId={projectId!}
      onSchliessen={() => setVorhaben(null)}
      onFertig={() => { setVorhaben(null); onFertig?.(); }}
    />
  ) : null;
  return { dialog, starten: setVorhaben };
}

function SteuerDialog({
  vorhaben,
  projectId,
  onSchliessen,
  onFertig,
}: {
  vorhaben: Vorhaben;
  projectId: string;
  onSchliessen: () => void;
  onFertig: () => void;
}) {
  const [grund, setGrund] = useState(vorhaben.vorschlag || '');
  const [cpcLimit, setCpcLimit] = useState('');
  const [budget, setBudget] = useState(vorhaben.typ === 'budget' ? String(vorhaben.aktuell ?? '') : '');

  const mut = useMutation({
    mutationFn: () => {
      let aktion: AdsAktion;
      if (vorhaben.typ === 'kampagne_status') {
        aktion = { art: 'kampagne_status', kampagne: vorhaben.kampagne, status: vorhaben.status };
      } else if (vorhaben.typ === 'anzeigengruppe_status') {
        aktion = { art: 'anzeigengruppe_status', anzeigengruppe_id: vorhaben.id, status: vorhaben.status };
      } else if (vorhaben.typ === 'gebotsstrategie') {
        const limit = parseFloat(cpcLimit.replace(',', '.'));
        aktion = {
          art: 'gebotsstrategie',
          kampagne: vorhaben.kampagne,
          strategie: vorhaben.strategie,
          cpc_limit_eur: vorhaben.strategie === 'MAXIMIZE_CLICKS' && Number.isFinite(limit) && limit > 0 ? limit : null,
        };
      } else if (vorhaben.typ === 'anzeigen_bereinigen') {
        aktion = { art: 'anzeigen_bereinigen', kampagne: vorhaben.kampagne, ersatztext: vorhaben.ersatztext };
      } else {
        const betrag = parseFloat(budget.replace(',', '.'));
        if (!Number.isFinite(betrag) || betrag <= 0) throw new Error('Bitte ein gültiges Tagesbudget eingeben');
        aktion = { art: 'budget', kampagne: vorhaben.kampagne, betrag_eur: betrag };
      }
      return steuereAds({ project_id: projectId, grund: grund.trim(), aktion });
    },
    onSuccess: (r) => {
      toast.success(r.warnung ? `${r.titel} — ${r.warnung}` : `${r.titel} · als Maßnahme erfasst`, {
        duration: r.warnung ? 10000 : 5000,
      });
      onFertig();
    },
    onError: (e: any) => toast.error(e?.message || 'Änderung fehlgeschlagen'),
  });

  const absenden = () => {
    if (grund.trim().length < 10) {
      toast.error('Bitte den Grund in einem Satz angeben — er erklärt dem Kunden im Monatsbericht, warum.');
      return;
    }
    mut.mutate();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onSchliessen}>
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-lg overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-900">{vorhaben.beschriftung}</h3>
          <button onClick={onSchliessen} className="text-gray-400 hover:text-gray-700">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-3">
          {vorhaben.typ === 'gebotsstrategie' &&
            vorhaben.strategie === 'MAXIMIZE_CONVERSIONS' &&
            (vorhaben.gezaehlteConversions ?? 0) < 5 && (
              <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-[12px] text-red-800">
                <strong>Achtung:</strong> Google zählt für diese Kampagne aktuell{' '}
                {vorhaben.gezaehlteConversions ? `nur ${vorhaben.gezaehlteConversions}` : 'keine'} Conversions in 30
                Tagen. „Conversions maximieren" ohne zählendes Signal nimmt der Automatik jedes Ziel — sie drosselt
                dann erfahrungsgemäß die Auslieferung bis auf null. Erst umstellen, wenn die Conversion-Zählung im
                Konto nachweislich funktioniert.
              </div>
            )}
          {vorhaben.typ === 'gebotsstrategie' && vorhaben.strategie === 'MAXIMIZE_CLICKS' && (
            <div>
              <label className="block text-[11px] text-gray-500 mb-1">Klickpreis-Deckel (€, empfohlen)</label>
              <input
                value={cpcLimit}
                onChange={(e) => setCpcLimit(e.target.value)}
                inputMode="decimal"
                placeholder="z. B. 1,50"
                className="w-32 px-3 py-2 border border-gray-300 rounded-lg text-sm"
              />
              <p className="text-[11px] text-gray-400 mt-1">
                Ohne Deckel kauft „Klicks maximieren" die billigsten Klicks — und billig heißt bei Google meist
                generisch oder daneben.
              </p>
            </div>
          )}

          {vorhaben.typ === 'budget' && (
            <div>
              <label className="block text-[11px] text-gray-500 mb-1">Neues Tagesbudget (€)</label>
              <input
                value={budget}
                onChange={(e) => setBudget(e.target.value)}
                inputMode="decimal"
                className="w-32 px-3 py-2 border border-gray-300 rounded-lg text-sm"
              />
            </div>
          )}

          <div>
            <label className="block text-[11px] text-gray-500 mb-1">
              Warum? <span className="text-gray-400">— Pflicht, wird zur Erklärung im Monatsbericht</span>
            </label>
            <textarea
              value={grund}
              onChange={(e) => setGrund(e.target.value)}
              rows={3}
              autoFocus
              placeholder="z. B. Die Gruppe holte fast nur Preisrecherche — 47 € in 11 Tagen ohne eine einzige Anfrage."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
            <p className="text-[11px] text-gray-400 mt-1">
              Vorausgefüllt aus den Zahlen dieser Zeile — bitte prüfen und ergänzen, was die Zahlen nicht sagen.
            </p>
          </div>
        </div>

        <div className="px-5 py-3 bg-gray-50 border-t border-gray-100 flex items-center justify-between gap-3">
          <p className="text-[11px] text-gray-500">
            Wird sofort im Google-Ads-Konto ausgeführt und als umgesetzte Maßnahme erfasst.
          </p>
          <div className="flex items-center gap-2 shrink-0">
            <button onClick={onSchliessen} className="px-3 py-1.5 text-xs text-gray-600 hover:text-gray-900">
              Abbrechen
            </button>
            <button
              onClick={absenden}
              disabled={mut.isPending}
              className="px-4 py-1.5 rounded-lg bg-primary-600 hover:bg-primary-700 text-white text-xs font-medium disabled:opacity-60 inline-flex items-center gap-1.5"
            >
              {mut.isPending && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              Übernehmen
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/** Aktionsknöpfe für eine Kampagnen- oder Anzeigengruppen-Zeile. */
export function ZeilenAktionen({
  row,
  starten,
}: {
  row: CampaignStat;
  starten: (v: Vorhaben) => void;
}) {
  const name = row.utm_campaign || '';
  const pausiert = row.status === 'PAUSED';
  // „Ohne Kampagne" ist eine Sammelzeile, keine echte Kampagne.
  if (!name || name.startsWith('Ohne Kampagne')) return null;

  if (!row.is_total) {
    if (!row.ad_group_id) return null;
    // Für eine Aktion im Konto zählt der Kontoname, nicht der Angle aus dem
    // Tracking — sonst steht im Dialog „kosten" statt „Badsanierung Kosten".
    const gruppenName = row.ad_group_name || row.angle || '';
    return (
      <button
        onClick={() =>
          starten({
            typ: 'anzeigengruppe_status',
            id: row.ad_group_id!,
            name: gruppenName,
            status: pausiert ? 'ENABLED' : 'PAUSED',
            beschriftung: `Anzeigengruppe „${gruppenName}" ${pausiert ? 'wieder aktivieren' : 'pausieren'}`,
            vorschlag: baueVorschlag(row, 'anzeigengruppe_status', pausiert ? 'ENABLED' : 'PAUSED'),
          })
        }
        title={pausiert ? 'Anzeigengruppe wieder aktivieren' : 'Anzeigengruppe pausieren'}
        className="p-1 text-gray-300 hover:text-gray-700 transition"
      >
        {pausiert ? <Play className="w-3.5 h-3.5" /> : <Pause className="w-3.5 h-3.5" />}
      </button>
    );
  }

  const aufConversions = row.bidding_strategy === 'MAXIMIZE_CONVERSIONS';
  return (
    <div className="flex items-center gap-0.5">
      <button
        onClick={() =>
          starten({
            typ: 'gebotsstrategie',
            kampagne: name,
            strategie: aufConversions ? 'MAXIMIZE_CLICKS' : 'MAXIMIZE_CONVERSIONS',
            gezaehlteConversions: row.ads_conversions_30d ?? null,
            beschriftung: `„${name}" auf ${aufConversions ? 'Klicks maximieren' : 'Conversions maximieren'} umstellen`,
            vorschlag: baueVorschlag(row, 'gebotsstrategie', aufConversions ? 'MAXIMIZE_CLICKS' : 'MAXIMIZE_CONVERSIONS'),
          })
        }
        title={`Läuft auf ${aufConversions ? 'Conversions maximieren' : 'Klicks maximieren'} — umstellen`}
        className={`p-1 transition ${aufConversions ? 'text-green-600 hover:text-green-700' : 'text-gray-300 hover:text-gray-700'}`}
      >
        <Target className="w-3.5 h-3.5" />
      </button>
      {/* Nur wenn Google die Texte beanstandet: Anzeigen bereinigen. Der Knopf
          erscheint genau dort, wo der Hinweis steht — Telefonnummern im Text
          drosseln die Auslieferung (Nicos Fall vom 31.08.2026). */}
      {/Richtlinie/i.test(row.auslieferung_hinweis || '') && (
        <button
          onClick={() =>
            starten({
              typ: 'anzeigen_bereinigen',
              kampagne: name,
              ersatztext: `${String(name).split('|')[0].trim()}: jetzt unverbindlich anfragen.`.slice(0, 60),
              beschriftung: `Anzeigentexte „${name}" bereinigen`,
              vorschlag: 'Google hat die Anzeigentexte eingeschränkt (Telefonnummer im Text ist nicht erlaubt) und die Auslieferung gedrosselt. Die beanstandeten Texte wurden entfernt und die Anzeige sauber neu aufgesetzt — der Anruf-Weg läuft weiterhin über die Anruf-Erweiterung.',
            })
          }
          title="Von Google beanstandete Texte entfernen (neue Anzeige, alte pausieren)"
          className="p-1 text-amber-500 hover:text-amber-700 transition"
        >
          <ShieldAlert className="w-3.5 h-3.5" />
        </button>
      )}
      <button
        onClick={() =>
          starten({
            typ: 'budget',
            kampagne: name,
            aktuell: row.budget_eur ?? null,
            beschriftung: `Tagesbudget „${name}" ändern`,
            vorschlag: baueVorschlag(row, 'budget'),
          })
        }
        title={row.budget_eur ? `Tagesbudget ${row.budget_eur} € — ändern` : 'Tagesbudget ändern'}
        className="p-1 text-gray-300 hover:text-gray-700 transition"
      >
        <Wallet className="w-3.5 h-3.5" />
      </button>
      <button
        onClick={() =>
          starten({
            typ: 'kampagne_status',
            kampagne: name,
            status: pausiert ? 'ENABLED' : 'PAUSED',
            beschriftung: `Kampagne „${name}" ${pausiert ? 'wieder aktivieren' : 'pausieren'}`,
            vorschlag: baueVorschlag(row, 'kampagne_status', pausiert ? 'ENABLED' : 'PAUSED'),
          })
        }
        title={pausiert ? 'Kampagne wieder aktivieren' : 'Kampagne pausieren'}
        className="p-1 text-gray-300 hover:text-gray-700 transition"
      >
        {pausiert ? <Play className="w-3.5 h-3.5" /> : <Pause className="w-3.5 h-3.5" />}
      </button>
    </div>
  );
}
